import { UserRole } from '@lib/graphql/documents.generated'
import { AdminRoutes, Routes } from '@lib/routes'
import { useLogout } from '@modules/auth/hooks/use-logout'
import { useMe } from '@modules/users/hooks/use-me'
import { boxShadow } from '@styles/box-shadow'
import { colors, ColorType } from '@styles/colors'
import { border, borderRadius, flexCenter, transition } from '@styles/utils'
import { PLACEMENT } from 'baseui/popover'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { padding } from 'polished'
import { useStyletron } from 'styletron-react'
import { BasePopover } from '../base-popover'
import { LabelS } from '../base-typography'
import { Icon, IconType } from '../icon'
import { mediaQuery } from '@styles/media-query'

interface HeaderMenuProps {
  menus: Array<{
    name: string
    icon?: IconType
    href?: string
    onClick?: () => void
    color?: ColorType
  }>
}

export const HeaderMenu = ({ menus }: HeaderMenuProps) => {
  const [css] = useStyletron()
  const { logout } = useLogout()
  const { me } = useMe()
  const router = useRouter()

  const menuItemStyle = css({
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    ...transition('background-color', '0.2s'),
    ...padding('16px', '16px'),
    ':hover': {
      backgroundColor: colors.gray50,
    },
  })

  return (
    <>
      <BasePopover
        dismissOnClickOutside
        dismissOnEsc
        triggerType="click"
        placement={PLACEMENT.bottomRight}
        accessibilityType="menu"
        content={({ close }) => {
          return (
            <ul
              className={css({
                ...border(colors.gray100),
                ...borderRadius(16),
                ...padding('4px', '0px'),
                backgroundColor: colors.white,
                minWidth: '200px',
                overflow: 'hidden',
              })}
            >
              <li
                className={css({
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  rowGap: '8px',
                  ...padding('20px', '16px'),
                  ...boxShadow('borderBottom'),
                })}
              >
                <LabelS>{me?.nickname}님</LabelS>
                <LabelS color={colors.gray400}>{me?.email}</LabelS>
              </li>
              {me?.role === UserRole.Admin && (
                <>
                  <li onClick={close}>
                    {router.pathname.includes('admin') ? (
                      <Link href={Routes.HOME}>
                        <a className={menuItemStyle}>
                          <LabelS flex="1 1 auto" color={colors.gray600}>
                            온톨로 이동하기
                          </LabelS>
                          <Icon type="ChevronRight" size={12} />
                        </a>
                      </Link>
                    ) : (
                      <Link href={AdminRoutes.HOME}>
                        <a className={menuItemStyle}>
                          <LabelS flex="1 1 auto" color={colors.gray600}>
                            관리자 페이지
                          </LabelS>
                          <Icon type="ChevronRight" size={12} />
                        </a>
                      </Link>
                    )}
                  </li>
                </>
              )}
              {menus.map(
                ({ name, icon, href, onClick, color = colors.gray700 }) => (
                  <li key={name} onClick={close}>
                    {href && (
                      <Link href={href}>
                        <a className={menuItemStyle}>
                          {/* {icon && <Icon type={icon} size={20} color={color} />} */}
                          <LabelS flex="1 1 auto" color={color}>
                            {name}
                          </LabelS>
                          {icon && <Icon type="ChevronRight" size={12} />}
                        </a>
                      </Link>
                    )}
                    {Boolean(onClick) && (
                      <div className={menuItemStyle} onClick={onClick}>
                        {icon && <Icon type={icon} size={20} color={color} />}
                        <LabelS flex="1 1 auto" color={color}>
                          {name}
                        </LabelS>
                        {icon && <Icon type="ChevronRight" size={12} />}
                      </div>
                    )}
                  </li>
                )
              )}
              <li>
                <div className={menuItemStyle} onClick={logout}>
                  <LabelS flex="1 1 auto">로그아웃</LabelS>
                </div>
              </li>
            </ul>
          )
        }}
      >
        <button
          aria-label="header-menu"
          className={css({
            ...flexCenter,
            cursor: 'pointer',
            columnGap: '8px',
            ...padding('7px', '12px'),
            ...border(colors.gray300),
            ...borderRadius(80),
            ...transition('border-color'),
            backgroundColor: colors.white,
            boxSizing: 'border-box',
            ':hover': {
              ...border(colors.gray500),
            },
            display: 'none',
            [mediaQuery.TABLET]: {
              display: 'flex',
            },
          })}
        >
          <Icon type="Menu" size={20} />
          <Icon type="Person" size={20} />
        </button>
      </BasePopover>
    </>
  )
}
